import {
  pickScratchBrushStamp,
  type ScratchBrushSet,
} from './scratch-brush'

export type { ScratchBrushSet } from './scratch-brush'
export { createScratchBrushSet } from './scratch-brush'

export const SCRATCH_AUTO_REVEAL_THRESHOLD = 0.1
export const SCRATCH_AUTO_REVEAL_DELAY_MS = 1000
export const SCRATCH_PROGRESS_SAMPLE_STEP = 8
export const SCRATCH_BRUSH_RADIUS_RATIO = 0.08
export const SCRATCH_MIN_BRUSH_RADIUS = 18
export const SCRATCH_MAX_BRUSH_RADIUS = 36
export const SCRATCH_STROKE_STEP_RATIO = 0.22
export const SCRATCH_SCATTER_STAMP_COUNT = 2

export type ScratchPoint = {
  x: number
  y: number
}

export function loadScratchImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.decoding = 'async'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Não foi possível carregar a imagem da raspadinha.'))
    image.src = src
  })
}

export function getScratchBrushRadius(canvasWidth: number): number {
  const scaled = canvasWidth * SCRATCH_BRUSH_RADIUS_RATIO
  return Math.min(SCRATCH_MAX_BRUSH_RADIUS, Math.max(SCRATCH_MIN_BRUSH_RADIUS, scaled))
}

export function setupScratchCanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): CanvasRenderingContext2D {
  const pixelRatio = window.devicePixelRatio || 1
  canvas.width = Math.floor(width * pixelRatio)
  canvas.height = Math.floor(height * pixelRatio)
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Não foi possível iniciar a área de raspagem.')
  }

  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  return context
}

export type ImageContainAnchor = 'top' | 'center'

export type ImageDisplayRect = {
  offsetX: number
  offsetY: number
  drawWidth: number
  drawHeight: number
}

export function computeImageContainRect(
  imageWidth: number,
  imageHeight: number,
  areaWidth: number,
  areaHeight: number,
  anchor: ImageContainAnchor = 'top'
): ImageDisplayRect {
  const imageRatio = imageWidth / imageHeight
  const areaRatio = areaWidth / areaHeight

  if (imageRatio > areaRatio) {
    const drawWidth = areaWidth
    const drawHeight = areaWidth / imageRatio

    return {
      offsetX: 0,
      offsetY: anchor === 'top' ? 0 : (areaHeight - drawHeight) / 2,
      drawWidth,
      drawHeight,
    }
  }

  const drawHeight = areaHeight
  const drawWidth = areaHeight * imageRatio

  return {
    offsetX: (areaWidth - drawWidth) / 2,
    offsetY: anchor === 'top' ? 0 : (areaHeight - drawHeight) / 2,
    drawWidth,
    drawHeight,
  }
}

export function drawImageCover(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number
): void {
  const imageRatio = image.width / image.height
  const areaRatio = width / height

  let drawWidth = width
  let drawHeight = height
  let offsetX = 0
  let offsetY = 0

  if (imageRatio > areaRatio) {
    drawWidth = height * imageRatio
    offsetX = (width - drawWidth) / 2
  } else {
    drawHeight = width / imageRatio
    offsetY = (height - drawHeight) / 2
  }

  context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight)
}

export function paintScratchResult(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
  frame: ImageDisplayRect,
  backgroundColor: string
): void {
  context.globalCompositeOperation = 'source-over'
  context.clearRect(0, 0, width, height)
  context.fillStyle = backgroundColor
  context.fillRect(0, 0, width, height)
  context.drawImage(image, frame.offsetX, frame.offsetY, frame.drawWidth, frame.drawHeight)
}

export function paintScratchCover(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
  options?: {
    backgroundColor?: string
    anchor?: ImageContainAnchor
    frame?: ImageDisplayRect
  }
): ImageDisplayRect {
  const anchor = options?.anchor ?? 'top'
  const frame =
    options?.frame ??
    computeImageContainRect(image.naturalWidth, image.naturalHeight, width, height, anchor)

  context.globalCompositeOperation = 'source-over'
  context.clearRect(0, 0, width, height)
  context.fillStyle = options?.backgroundColor ?? '#171717'
  context.fillRect(0, 0, width, height)
  context.drawImage(image, frame.offsetX, frame.offsetY, frame.drawWidth, frame.drawHeight)

  return frame
}

export function interpolateScratchPoints(from: ScratchPoint, to: ScratchPoint, step: number): ScratchPoint[] {
  const deltaX = to.x - from.x
  const deltaY = to.y - from.y
  const distance = Math.hypot(deltaX, deltaY)

  if (distance <= step) {
    return [to]
  }

  const points: ScratchPoint[] = []
  const steps = Math.ceil(distance / step)

  for (let index = 1; index <= steps; index += 1) {
    const progress = index / steps
    points.push({
      x: from.x + deltaX * progress,
      y: from.y + deltaY * progress,
    })
  }

  return points
}

export function eraseScratchAtPoint(
  context: CanvasRenderingContext2D,
  point: ScratchPoint,
  brushSet: ScratchBrushSet,
  seed: number
): void {
  const { stamp, rotation } = pickScratchBrushStamp(brushSet, seed)
  const halfSize = brushSet.drawSize / 2

  context.save()
  context.globalCompositeOperation = 'destination-out'
  context.translate(point.x, point.y)
  context.rotate(rotation)
  context.drawImage(stamp, -halfSize, -halfSize)
  context.restore()

  for (let scatterIndex = 0; scatterIndex < SCRATCH_SCATTER_STAMP_COUNT; scatterIndex += 1) {
    const scatterSeed = seed + scatterIndex * 17 + 3
    const scatterStamp = pickScratchBrushStamp(brushSet, scatterSeed)
    const scatterAngle = scatterSeed * 0.31
    const scatterDistance = brushSet.diameter * (0.12 + (scatterSeed % 7) * 0.04)
    const offsetX = Math.cos(scatterAngle) * scatterDistance
    const offsetY = Math.sin(scatterAngle) * scatterDistance
    const scatterScale = 0.42 + (scatterSeed % 5) * 0.08

    context.save()
    context.globalCompositeOperation = 'destination-out'
    context.translate(point.x + offsetX, point.y + offsetY)
    context.rotate(scatterStamp.rotation)
    context.scale(scatterScale, scatterScale)
    context.drawImage(scatterStamp.stamp, -halfSize, -halfSize)
    context.restore()
  }
}

export function eraseScratchStroke(
  context: CanvasRenderingContext2D,
  from: ScratchPoint,
  to: ScratchPoint,
  brushSet: ScratchBrushSet,
  seedBase: number
): void {
  const step = Math.max(2, brushSet.diameter * SCRATCH_STROKE_STEP_RATIO)
  const points = interpolateScratchPoints(from, to, step)

  points.forEach((point, index) => {
    const seed = seedBase + Math.round(point.x * 3.7 + point.y * 2.3) + index
    eraseScratchAtPoint(context, point, brushSet, seed)
  })
}

export function estimateScratchProgress(
  canvas: HTMLCanvasElement,
  sampleStep = SCRATCH_PROGRESS_SAMPLE_STEP
): number {
  const context = canvas.getContext('2d')
  if (!context) {
    return 0
  }

  const width = canvas.width
  const height = canvas.height
  if (width === 0 || height === 0) {
    return 0
  }

  const imageData = context.getImageData(0, 0, width, height)
  const { data } = imageData
  let transparentSamples = 0
  let totalSamples = 0

  for (let y = 0; y < height; y += sampleStep) {
    for (let x = 0; x < width; x += sampleStep) {
      const alpha = data[(y * width + x) * 4 + 3] ?? 255
      if (alpha < 32) {
        transparentSamples += 1
      }
      totalSamples += 1
    }
  }

  if (totalSamples === 0) {
    return 0
  }

  return transparentSamples / totalSamples
}

export function getPointerPosition(
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number
): ScratchPoint {
  const rect = canvas.getBoundingClientRect()
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  }
}

export function hasReachedScratchRevealThreshold(progress: number): boolean {
  return progress >= SCRATCH_AUTO_REVEAL_THRESHOLD
}
